import { Suspense, lazy } from 'react';
import { KeyRound, Link2 } from 'lucide-react';
import { H1, H2, P, UL, LI, Code, Note } from './primitives';
import { ERD_GROUP_LABELS, type ErdGroup } from './erd/erd-model';

// Heavy: React Flow only loads when this section mounts, off the app's main
// bundle.
const ErdCanvas = lazy(() => import('./erd/ErdCanvas'));

const GROUP_DOT: Record<ErdGroup, string> = {
  core: 'bg-ink',
  loop: 'bg-info',
  prototype: 'bg-success',
  config: 'bg-ink-faint',
  analytics: 'bg-warning',
};

function Legend() {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-line bg-surface-2 px-4 py-3 text-xs text-ink-muted">
      {(Object.keys(ERD_GROUP_LABELS) as ErdGroup[]).map((g) => (
        <span key={g} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-sm ${GROUP_DOT[g]}`} />
          {ERD_GROUP_LABELS[g]}
        </span>
      ))}
      <span className="mx-1 h-4 w-px bg-line" aria-hidden="true" />
      <span className="inline-flex items-center gap-1.5">
        <KeyRound className="h-3.5 w-3.5 text-warning" /> primary key
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Link2 className="h-3.5 w-3.5 text-info" /> foreign key
      </span>
    </div>
  );
}

export function SectionDataModel() {
  return (
    <>
      <H1>Data Model</H1>
      <P>
        Everything the platform knows lives in Postgres — 23 tables, managed with
        Prisma. Postgres is the single source of truth; Redis only holds the
        transient job queue and can be wiped without losing history. This is the
        whole schema, laid out as an entity-relationship diagram.
      </P>
      <P>
        The <strong className="text-ink">core spine</strong> runs left to right:
        an <Code>Org</Code> owns <Code>Project</Code>s, each project holds{' '}
        <Code>Issue</Code>s, each issue spawns <Code>Run</Code>s, and every run
        fans out into <Code>Stage</Code>s and <Code>StageAttempt</Code>s (one per
        gate iteration, carrying the gate outcome the timeline is built from).
      </P>

      <Legend />

      <Suspense
        fallback={
          <div className="mb-4 grid h-[560px] w-full place-items-center rounded-lg border border-line bg-surface-0 text-sm text-ink-faint">
            Loading schema diagram…
          </div>
        }
      >
        <ErdCanvas />
      </Suspense>

      <P>
        Drag a table to rearrange it, scroll to zoom, hover one to spotlight its
        relationships, and click a table to jump to the section that explains it.
      </P>

      <H2>How the tables relate</H2>
      <UL>
        <LI>
          <strong className="text-ink">Multi-tenancy root</strong> — every
          tenant-owned table carries a denormalized <Code>orgId</Code> (→{' '}
          <Code>Org</Code>), and Postgres <strong className="text-ink">row-level
          security</strong> enforces that a workspace can only ever see its own
          rows. <Code>Membership</Code> ties a <Code>User</Code> to their
          workspace with a role (<Code>owner</Code> / <Code>member</Code>).
          Platform operators live on the separate ops plane (<Code>Operator</Code>{' '}
          table), never in <Code>User</Code>.{' '}
          <Code>OrgSetting</Code> holds each workspace's
          own BYO secrets (Claude / GitHub / Telegram), encrypted. Deleting an
          org cascades to everything it owns.
        </LI>
        <LI>
          <strong className="text-ink">Core pipeline</strong> —{' '}
          <Code>Org → Project → Issue → Run → Stage → StageAttempt</Code>,
          plus <Code>LogLine</Code> (replayable output) and <Code>IssueEvent</Code>{' '}
          (the governance trail). Deleting a project cascades to its issues, but
          its runs are kept — detached, with the project&apos;s name and kind
          snapshotted — so metrics, cost history and the monthly spend cap
          still count them.
        </LI>
        <LI>
          <strong className="text-ink">Per-project knowledge</strong> — each project
          has at most one <Code>ProjectMemory</Code> (agent lessons, 32 KB) and one{' '}
          <Code>ProjectWiki</Code> (architecture map, 8 KB); both are upserted by
          the runner from <Code>/aiai/memory.md</Code> and{' '}
          <Code>/aiai/wiki.md</Code> respectively.
        </LI>
        <LI>
          <strong className="text-ink">Scheduled loops</strong> — a{' '}
          <Code>Loop</Code> belongs to a project and stamps its id onto every{' '}
          <Code>Issue</Code> it fires, so a loop's output is always traceable.
        </LI>
        <LI>
          <strong className="text-ink">Prototype deploys</strong> — a project has
          at most one <Code>PrototypeDeployment</Code> (1:1), which owns its{' '}
          <Code>PrototypeSecret</Code>s and <Code>PrototypeCustomDomain</Code>s.
        </LI>
        <LI>
          <strong className="text-ink">Config &amp; auth</strong> —{' '}
          <Code>User</Code> and <Code>Setting</Code> hold global configuration;
          secrets live in <Code>*Enc</Code> columns as AES-256-GCM ciphertext.
        </LI>
        <LI>
          <strong className="text-ink">Analytics</strong> — <Code>UiEvent</Code>{' '}
          is an append-only event table (screen visits + interactions,
          discriminated by <Code>type</Code>) the Metrics view aggregates.
        </LI>
      </UL>

      <Note>
        The diagram mirrors <Code>packages/api/prisma/schema.prisma</Code> — the
        authoritative schema. A few operational columns are omitted from the
        cards for legibility; the migration files carry the exact shape.
      </Note>
    </>
  );
}
